import { useEffect, useState } from 'react';

import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import { useFlags } from '@/hooks/useFlags';

import Constants from '@/constants';

import {
  parseSmartSearchPromptSuggestionEntry,
  resolveSmartSearchPromptText,
} from '../resolveSmartSearchPromptText';

import { userPrefsAtom } from '@/atoms/userPrefs';

const ROTATION_INTERVAL_MS = 4_000;

const getPromptAtIndex = (
  prompts: string[],
  index: number,
): string | undefined => {
  if (prompts.length === 0) return undefined;
  const normalizedIndex =
    ((index % prompts.length) + prompts.length) % prompts.length;
  return prompts[normalizedIndex];
};

interface UseSmartSearchPromptSuggestionsOptions {
  paused?: boolean;
  enabled?: boolean;
}

const noopResetToTop = () => undefined;

export const useSmartSearchPromptSuggestions = ({
  paused = false,
  enabled = true,
}: UseSmartSearchPromptSuggestionsOptions = {}) => {
  const { smartSearchPromptSuggestions } = useFlags();
  const { i18n } = useTranslation();
  const { language: userLanguage } = useAtomValue(userPrefsAtom);

  const language =
    userLanguage || i18n.language || Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE;

  const suggestions = Array.isArray(smartSearchPromptSuggestions)
    ? smartSearchPromptSuggestions
    : [];

  const prompts = enabled
    ? suggestions
        .map(entry => parseSmartSearchPromptSuggestionEntry(entry))
        .filter(
          (entry): entry is NonNullable<typeof entry> => entry !== undefined,
        )
        .map(entry => resolveSmartSearchPromptText(entry, i18n, language))
        .filter((prompt): prompt is string => prompt !== undefined)
    : [];
  const promptsKey = prompts.join('\0');

  const [rotation, setRotation] = useState({ promptsKey, index: 0 });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const index = rotation.promptsKey === promptsKey ? rotation.index : 0;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  const shouldRotate =
    enabled && prompts.length > 1 && !paused && !prefersReducedMotion;

  useEffect(() => {
    if (!shouldRotate) return;

    const intervalId = window.setInterval(() => {
      setRotation(current => ({
        promptsKey,
        index: (current.promptsKey === promptsKey ? current.index : 0) + 1,
      }));
    }, ROTATION_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [shouldRotate, prompts.length, promptsKey]);

  const resetToTop = () => {
    setRotation({ promptsKey, index: 0 });
  };

  const effectiveIndex = prefersReducedMotion ? 0 : index;
  const currentPrompt = getPromptAtIndex(prompts, effectiveIndex);

  return {
    currentPrompt: enabled ? currentPrompt : undefined,
    resetToTop: enabled ? resetToTop : noopResetToTop,
  };
};
